import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Input, message } from 'antd';
import { ArrowLeftOutlined, RocketOutlined, BellOutlined } from '@ant-design/icons';

const ComingSoonContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    #181A20 0%,
    #1a1d26 25%,
    #0f1115 50%,
    #1a1d26 75%,
    #181A20 100%
  );
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 20% 20%,
      rgba(14, 203, 129, 0.05) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 80%,
      rgba(14, 203, 129, 0.03) 0%,
      transparent 50%
    );
    pointer-events: none;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 800px;
  width: 100%;
  text-align: center;
`;

const BackButton = styled(Button)`
  position: absolute;
  top: 40px;
  left: 40px;
  background-color: transparent !important;
  border: 1px solid #474D57 !important;
  color: #EAECEF !important;
  height: 40px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 2;

  &:hover {
    border-color: #0ECB81 !important;
    color: #0ECB81 !important;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 40px;

  img {
    height: 60px;
    width: auto;
  }

  span {
    font-size: 36px;
    font-weight: 700;
    color: #0ECB81;
  }
`;

const IconWrapper = styled.div`
  font-size: 80px;
  color: #0ECB81;
  margin-bottom: 30px;
  animation: float 3s ease-in-out infinite;

  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-20px);
    }
  }
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 700;
  color: #EAECEF;
  margin-bottom: 20px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  color: #848E9C;
  margin-bottom: 40px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CountdownContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 50px;
  flex-wrap: wrap;
`;

const CountdownItem = styled.div`
  background-color: #1E2329;
  border: 1px solid #2B3139;
  border-radius: 12px;
  padding: 20px 30px;
  min-width: 100px;

  @media (max-width: 768px) {
    padding: 15px 20px;
    min-width: 80px;
  }
`;

const CountdownNumber = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #0ECB81;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const CountdownLabel = styled.div`
  font-size: 14px;
  color: #848E9C;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const NotifySection = styled.div`
  background-color: #1E2329;
  border: 1px solid #2B3139;
  border-radius: 12px;
  padding: 40px;
  margin-top: 40px;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const NotifyTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #EAECEF;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const NotifySubtitle = styled.p`
  font-size: 16px;
  color: #848E9C;
  margin-bottom: 30px;
`;

const NotifyForm = styled.div`
  display: flex;
  gap: 12px;
  max-width: 500px;
  margin: 0 auto;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const StyledInput = styled(Input)`
  height: 48px;
  background-color: #2B3139 !important;
  border: 1px solid #474D57 !important;
  color: #EAECEF !important;
  font-size: 16px;

  &::placeholder {
    color: #848E9C !important;
  }

  &:hover {
    border-color: #0ECB81 !important;
  }

  &:focus {
    border-color: #0ECB81 !important;
    box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.1) !important;
  }

  input {
    background-color: transparent !important;
    color: #EAECEF !important;
  }
`;

const NotifyButton = styled(Button)`
  height: 48px;
  background-color: #0ECB81 !important;
  border: none !important;
  color: #0B0E11 !important;
  font-weight: 600;
  padding: 0 40px;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8 !important;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FeaturesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 50px;
`;

const FeatureItem = styled.div`
  background-color: rgba(14, 203, 129, 0.05);
  border: 1px solid rgba(14, 203, 129, 0.2);
  border-radius: 8px;
  padding: 20px;
  text-align: center;

  h4 {
    color: #0ECB81;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  p {
    color: #848E9C;
    font-size: 14px;
    margin: 0;
  }
`;

const ComingSoonPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [countdown, setCountdown] = useState({
    days: 30,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Countdown timer
  useEffect(() => {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + 30); // 30 days from now

    const interval = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;

      if (distance < 0) {
        clearInterval(interval);
        return;
      }

      setCountdown({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleNotify = () => {
    if (!email) {
      message.error('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      message.error('Please enter a valid email address');
      return;
    }

    // TODO: Send email to backend
    message.success('Thank you! We\'ll notify you when we launch.');
    setEmail('');
  };

  return (
    <ComingSoonContainer>
      <BackButton onClick={() => navigate('/')} icon={<ArrowLeftOutlined />}>
        Back to Home
      </BackButton>

      <ContentWrapper>
        <LogoContainer>
          <img src="/Algotrade - Black.png" alt="Algotrade" />
          <span>Fortuna</span>
        </LogoContainer>

        <IconWrapper>
          <RocketOutlined />
        </IconWrapper>

        <Title>Something Amazing is Coming Soon!</Title>
        <Subtitle>
          We're working hard to bring you an incredible pair trading experience.
          Stay tuned for the launch!
        </Subtitle>

        <CountdownContainer>
          <CountdownItem>
            <CountdownNumber>{countdown.days}</CountdownNumber>
            <CountdownLabel>Days</CountdownLabel>
          </CountdownItem>
          <CountdownItem>
            <CountdownNumber>{countdown.hours}</CountdownNumber>
            <CountdownLabel>Hours</CountdownLabel>
          </CountdownItem>
          <CountdownItem>
            <CountdownNumber>{countdown.minutes}</CountdownNumber>
            <CountdownLabel>Minutes</CountdownLabel>
          </CountdownItem>
          <CountdownItem>
            <CountdownNumber>{countdown.seconds}</CountdownNumber>
            <CountdownLabel>Seconds</CountdownLabel>
          </CountdownItem>
        </CountdownContainer>

        <NotifySection>
          <NotifyTitle>
            <BellOutlined />
            Get Notified
          </NotifyTitle>
          <NotifySubtitle>
            Be the first to know when we launch. Enter your email below!
          </NotifySubtitle>
          <NotifyForm>
            <StyledInput
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onPressEnter={handleNotify}
              size="large"
            />
            <NotifyButton onClick={handleNotify}>
              Notify Me
            </NotifyButton>
          </NotifyForm>
        </NotifySection>

        <FeaturesList>
          <FeatureItem>
            <h4>Advanced Analytics</h4>
            <p>Real-time market data and insights</p>
          </FeatureItem>
          <FeatureItem>
            <h4>Smart Execution</h4>
            <p>Automated pair trading strategies</p>
          </FeatureItem>
          <FeatureItem>
            <h4>Risk Management</h4>
            <p>Built-in portfolio protection</p>
          </FeatureItem>
        </FeaturesList>
      </ContentWrapper>
    </ComingSoonContainer>
  );
};

export default ComingSoonPage;

